import { clsx } from 'clsx'
import {
  useRef,
  useEffect,
  type ComponentPropsWithRef,
  type ReactElement,
  type RefObject,
} from 'react'

import style from './fireflies.module.css'

type Particle = {
  x: number
  y: number
  xv: number
  yv: number
  s: number
  life: number
  lifetime: number
  phase: number
  flickerSpeed: number
}

function isWindow(target: HTMLElement | Window): target is Window {
  return typeof window !== 'undefined' && target === window
}

export interface FirefliesProps extends ComponentPropsWithRef<'canvas'> {
  targetRef?: RefObject<HTMLElement | null>
}

export function Fireflies(props: FirefliesProps): ReactElement {
  const { className, targetRef, ...rest } = props

  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const sizeScale = 1.2
  const spawnRate = 2 // particles per second
  const lifetimeMs = 2800
  const speedScale = 0.2
  const maxParticles = 400
  const wanderStrength = 0.195
  const followStrength = 0.08
  const emissionRadius = 28

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Get the target element from the ref, or default to window
    const target = targetRef?.current || window

    // Memoize calculations to avoid repeated computations in hot paths
    const TWO_PI = Math.PI * 2
    const wander = wanderStrength
    const follow = followStrength
    const maxSpeed = 1 + 6 * speedScale
    const vScale = speedScale
    const dead = 900 // ~30px dead-zone for cursor follow

    // Debounce resize to avoid excessive canvas resizing
    let pendingResize = false
    let resizeRafId = 0

    const performResize = () => {
      let width: number, height: number

      if (isWindow(target)) {
        // Fill the entire viewport
        width = window.innerWidth
        height = window.innerHeight
      } else {
        // Fill the target element's dimensions
        const rect = target.getBoundingClientRect()
        width = rect.width
        height = rect.height
      }

      canvas.width = width
      canvas.height = height
      pendingResize = false
    }

    const onResize = () => {
      if (!pendingResize) {
        pendingResize = true
        resizeRafId = window.requestAnimationFrame(performResize)
      }
    }

    performResize()

    // Initialize mouse.out to true - particles only spawn when mouse actually enters
    let mouse = { x: canvas.width / 2, y: canvas.height / 2, out: true }
    let isPageVisible = !document.hidden

    const handleMouseMove = (e: Event) => {
      if (!(e instanceof MouseEvent)) {
        return
      }

      const mouseEvent = e
      let x: number, y: number

      if (isWindow(target)) {
        // Use client coordinates directly for window
        x = mouseEvent.clientX
        y = mouseEvent.clientY
      } else {
        // Calculate coordinates relative to target element
        const rect = target.getBoundingClientRect()
        x = mouseEvent.clientX - rect.left
        y = mouseEvent.clientY - rect.top
      }

      mouse = { x, y, out: false }
      // Wake up animation if it was sleeping
      if (!rafId && isPageVisible) {
        lastTime = performance.now()
        rafId = window.requestAnimationFrame(loop)
      }
    }
    const handleMouseOut = () => {
      mouse.out = true
    }

    const handleVisibilityChange = () => {
      isPageVisible = !document.hidden
      if (isPageVisible) {
        // Resume animation when tab becomes visible
        if (!rafId) {
          lastTime = performance.now()
          rafId = window.requestAnimationFrame(loop)
        }
      } else {
        // Pause animation when tab is hidden
        if (rafId) {
          window.cancelAnimationFrame(rafId)
          rafId = 0
        }
      }
    }

    // Set up resize handling based on target type
    if (isWindow(target)) {
      window.addEventListener('resize', onResize)
    } else {
      // Use ResizeObserver for HTML elements
      resizeObserverRef.current = new ResizeObserver(() => {
        onResize()
      })
      resizeObserverRef.current.observe(target)
    }

    target.addEventListener('mousemove', handleMouseMove)
    target.addEventListener('mouseout', handleMouseOut)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    let particles: Particle[] = []
    let spawnAccumulator = 0 // fractional spawns based on spawnRate (per second)
    let type = 0
    let lastTime = 0
    let rafId = 0

    const newParticle = () => {
      type = type ? 0 : 1
      // spawn within a disk around the cursor (Poisson-like via sqrt for uniform area)
      const angle = Math.random() * TWO_PI
      const radius = Math.sqrt(Math.random()) * emissionRadius
      const sx = mouse.x + Math.cos(angle) * radius
      const sy = mouse.y + Math.sin(angle) * radius
      // outward-biased initial velocity + some tangential noise
      const outward = 0.6 + Math.random() * 0.6
      const tangential = (Math.random() - 0.5) * 0.6
      const vx = (Math.cos(angle) * outward - Math.sin(angle) * tangential) * vScale
      const vy = (Math.sin(angle) * outward + Math.cos(angle) * tangential) * vScale
      const base = type ? 1.3 : 1.6
      particles.push({
        x: sx,
        y: sy,
        xv: vx,
        yv: vy,
        s: (type ? base + Math.random() : base) * sizeScale * (1 + Math.random()), // 1x–2x size variation
        life: 0,
        lifetime: Math.max(300, lifetimeMs),
        phase: Math.random() * TWO_PI,
        flickerSpeed: 1 + Math.random() * 2,
      })
    }

    // More saturated warm peach → warm white
    const startColor = { r: 0xf3, g: 0xb4, b: 0x8e }
    const endColor = { r: 0xff, g: 0xf2, b: 0xe6 }
    const draw = () => {
      // Early exit: skip drawing if no particles exist
      if (particles.length === 0) return

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Performance: Removed expensive per-particle shadow operations
      // Using simpler rendering without shadows for better performance
      for (const p of particles) {
        const t = Math.max(0, Math.min(1, p.life / p.lifetime))
        // Ease color so saturation lasts longer
        const ct = Math.pow(t, 0.6)
        const r = Math.round(startColor.r + (endColor.r - startColor.r) * ct)
        const g = Math.round(startColor.g + (endColor.g - startColor.g) * ct)
        const b = Math.round(startColor.b + (endColor.b - startColor.b) * ct)
        const flicker = 0.6 + 0.4 * Math.sin(p.phase)
        ctx.globalAlpha = flicker
        ctx.fillStyle = `rgb(${r},${g},${b})`
        ctx.beginPath()
        ctx.arc(p.x, p.y, p.s, 0, TWO_PI)
        ctx.fill()
      }
      ctx.globalAlpha = 1
    }

    const calculate = (time: number) => {
      const dt = Math.min(100, time - lastTime)
      lastTime = time

      if (!mouse.out) {
        // Spawn based on particles per second
        spawnAccumulator += (dt / 1000) * Math.max(0, spawnRate)
        for (; spawnAccumulator >= 1; spawnAccumulator -= 1) newParticle()
      }

      // Performance: Replace splice with slice for overflow removal (avoid O(n) reindexing)
      const particleOverflow = particles.length - maxParticles
      if (particleOverflow > 0) {
        particles = particles.slice(particleOverflow)
      }

      for (const p of particles) {
        // gentle wander (cursor-independent) like fireflies
        p.xv += (Math.random() - 0.5) * wander
        p.yv += (Math.random() - 0.5) * wander
        // soft cursor follow with dead-zone and mild repulsion to avoid clustering
        if (!mouse.out) {
          const dx = mouse.x - p.x
          const dy = mouse.y - p.y
          const d2 = dx * dx + dy * dy
          if (d2 > dead) {
            const inv = 1 / Math.sqrt(d2)
            p.xv += dx * inv * follow * 0.2
            p.yv += dy * inv * follow * 0.2
          } else {
            // within dead-zone, apply gentle repulsion to prevent stack
            const inv = 1 / Math.max(1, Math.sqrt(d2))
            p.xv -= dx * inv * follow * 0.15
            p.yv -= dy * inv * follow * 0.15
          }
        }
        // light drag
        p.xv *= 0.985
        p.yv *= 0.985
        // clamp velocity for stability
        const speed = Math.hypot(p.xv, p.yv)
        if (speed > maxSpeed) {
          const s = maxSpeed / speed
          p.xv *= s
          p.yv *= s
        }
        // soft screen bounds (steer back inside)
        if (p.x < 0 || p.x > canvas.width) p.xv *= -0.8
        if (p.y < 0 || p.y > canvas.height) p.yv *= -0.8
        p.x += p.xv
        p.y += p.yv
        p.life += dt
        p.phase += p.flickerSpeed * (dt / 1000)
      }
      // Performance: Use swap-and-pop technique for removing expired particles (avoid O(n) splice)
      for (let i = particles.length - 1; i >= 0; i--) {
        if (particles[i].life >= particles[i].lifetime) {
          // Swap with last element and pop
          const last = particles.at(-1)
          if (last) particles[i] = last
          particles.pop()
        }
      }
    }

    const loop = (time: number) => {
      draw()
      calculate(time)

      // Performance: Sleep mode - stop animation when no particles and mouse is out
      // Check after calculate() to ensure last particle is properly removed
      if (particles.length === 0 && mouse.out) {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        rafId = 0
        return
      }

      rafId = window.requestAnimationFrame(loop)
    }

    const start = (time: number) => {
      lastTime = time
      rafId = window.requestAnimationFrame(loop)
    }

    // Don't start animation loop if mouse is out (will start on first mousemove)
    if (!mouse.out) {
      rafId = window.requestAnimationFrame(start)
    }

    return () => {
      window.cancelAnimationFrame(rafId)
      window.cancelAnimationFrame(resizeRafId)

      // Clean up resize handling based on target type
      if (isWindow(target)) {
        window.removeEventListener('resize', onResize)
      } else {
        // Disconnect ResizeObserver for HTML elements
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect()
          resizeObserverRef.current = null
        }
      }

      target.removeEventListener('mousemove', handleMouseMove)
      target.removeEventListener('mouseout', handleMouseOut)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [targetRef])

  return <canvas ref={canvasRef} className={clsx(style.fireflies, className)} {...rest} />
}
